<script setup lang="ts">
import type { IncorporationLine, IncorporationOrder } from '@/api/erp-integration';
import ResponsiveDialog from '@/components/responsive-dialog.vue';
import { formatRange, quantityText } from './erp-format';

// The Sales orders as Odoo has them (notes and lines), open from any step of
// the incorporation screen.

const props = defineProps<{
  orders: IncorporationOrder[];
  lines: IncorporationLine[];
}>();
const open = defineModel<boolean>({ required: true });

function linesOf(order: IncorporationOrder): IncorporationLine[] {
  return props.lines.filter((line) => line.orderId === order.id);
}
</script>
<template>
  <responsive-dialog
    v-model="open"
    title="Presupuesto en Odoo"
    max-width="720"
    scrollable
  >
    <v-card
      v-for="order in orders"
      :key="order.id"
      flat
      class="mb-3 border"
    >
      <v-card-text>
        <div class="text-subtitle-2 font-weight-bold">
          {{ order.reference }}<template v-if="order.patientLabel">
            · {{ order.patientLabel }}
          </template>
        </div>
        <div class="text-caption text-medium-emphasis mb-3">
          {{ formatRange(order.startsOn, order.endsOn) }} (las fechas de los requerimientos siguen a
          Odoo)
        </div>
        <h3 class="text-overline">
          Líneas
        </h3>
        <ul class="lines mb-3">
          <li
            v-for="line in linesOf(order)"
            :key="line.id"
            class="text-body-2"
          >
            {{ line.productName ?? 'Producto sin nombre' }} ·
            {{ quantityText(line.quantity, line.uom) }}
          </li>
        </ul>
        <h3 class="text-overline">
          Notas
        </h3>
        <div
          v-if="order.notes"
          class="text-body-2 notes"
        >
          {{ order.notes }}
        </div>
        <div
          v-else
          class="text-body-2 text-medium-emphasis"
        >
          Sin notas.
        </div>
      </v-card-text>
    </v-card>
    <template #actions>
      <v-spacer />
      <v-btn
        variant="text"
        @click="open = false"
      >
        Cerrar
      </v-btn>
    </template>
  </responsive-dialog>
</template>
<style scoped>
.notes {
  white-space: pre-wrap;
}

.lines {
  padding-left: 20px;
}
</style>
